/**
 * CostBreakdownPanel — segmented budget bar + per-bucket rows for the
 * "LLM cost" insight card. Replaces the single-number CostGauge.
 *
 * Two dimensions, switched by a toggle (same pattern as TrendChart's
 * QoQ/WoW toggle):
 *   feature — the five additive buckets of cost_breakdown; the Other row
 *             expands to its per-source split (other_sources).
 *   model   — cost_breakdown.by_model, one row per model id; rows whose
 *             parent predates model tracking show as "unknown (pre-tracking)".
 *
 * Color tiers are keyed off the soft limit (settings-driven), not a fixed
 * percentage:
 *   < softPct        → success (green)
 *   softPct – 100%   → warning (amber)
 *   ≥ 100%           → error   (red)
 * When softPct is 0 (no soft limit or no budget configured) the amber band
 * collapses and the bar stays green until 100%.
 * A tick mark on the bar (`soft-limit-tick`) marks softPct so the soft
 * limit is visible even when the color tier alone wouldn't convey it.
 *
 * Segment colors follow the entity, never its rank: features and known
 * model ids each have a fixed color; gray is reserved for the semantic
 * catch-all buckets (Other / unknown). Identity is never color-alone —
 * every segment has a labeled row with its value.
 */

import { useState } from 'react';
import { Box, Collapse, Stack, Tooltip, Typography } from '@mui/material';

import type { CostBreakdown, CostItem } from '@/types/insights';

export interface CostBreakdownPanelProps {
  costUsd: number;
  budgetUsd: number;
  softLimitUsd: number;
  periodLabel: string;
  breakdown: CostBreakdown;
}

type Dimension = 'feature' | 'model';

interface Segment {
  key: string;
  label: string;
  color: string;
  costUsd: number;
}

// House series palette (TrendChart SERIES_COLORS + HealthHero tile family).
// Gray is reserved for the semantic catch-all (Other / unknown), never a
// regular series.
const CHROMATIC = ['#D97757', '#6BFFAB', '#6FA8FF', '#FFB000'] as const;
const NEUTRAL_GRAY = '#8a8a94';

const FEATURE_CONFIG: ReadonlyArray<{
  key: keyof Pick<
    CostBreakdown,
    'analyses' | 'investigations' | 'pr_reviews' | 'pr_replies' | 'other'
  >;
  label: string;
  color: string;
}> = [
  { key: 'analyses', label: 'Analyses', color: CHROMATIC[0] },
  { key: 'investigations', label: 'Investigations', color: CHROMATIC[1] },
  { key: 'pr_reviews', label: 'PR reviews', color: CHROMATIC[2] },
  { key: 'pr_replies', label: 'PR replies', color: CHROMATIC[3] },
  { key: 'other', label: 'Other', color: NEUTRAL_GRAY },
];

// Fixed model → color mapping so a model keeps its color as the list
// grows/shrinks month to month; unregistered future models fall back to
// palette order.
const MODEL_COLOR_MAP: Record<string, string> = {
  'claude-sonnet-4-6': CHROMATIC[0],
  'claude-opus-4-7': CHROMATIC[2],
  'claude-haiku-4-5': CHROMATIC[1],
  unknown: NEUTRAL_GRAY,
};

function modelColor(key: string, index: number): string {
  return MODEL_COLOR_MAP[key] ?? CHROMATIC[index % CHROMATIC.length] ?? CHROMATIC[0];
}

function modelLabel(key: string): string {
  return key === 'unknown' ? 'unknown (pre-tracking)' : key;
}

function formatUsd(value: number): string {
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function getColor(pct: number, softPct: number): 'success' | 'warning' | 'error' {
  if (pct >= 100) return 'error';
  // softPct === 0 means no soft limit (or no budget) is configured — both
  // pct and softPct derive from budgetUsd > 0 — so stay green rather than
  // false-warn on an unconfigured card.
  if (softPct > 0 && pct >= softPct) return 'warning';
  return 'success';
}

function pctOf(part: number, total: number): string {
  if (total <= 0) return '0%';
  const pct = (part / total) * 100;
  if (part > 0 && pct < 0.5) return '<1%'; // don't render a real cost as "0%"
  return `${pct.toFixed(0)}%`;
}

const MONO = '"JetBrains Mono", monospace';

// ---------------------------------------------------------------------------
// Internal pieces
// ---------------------------------------------------------------------------

interface SegmentedBudgetBarProps {
  segments: Segment[];
  costUsd: number;
  budgetUsd: number;
  pct: number;
  softPct: number;
}

function SegmentedBudgetBar({
  segments,
  costUsd,
  budgetUsd,
  pct,
  softPct,
}: SegmentedBudgetBarProps) {
  // Widths are budget-relative; over budget (or no budget) they scale
  // proportionally so the filled portion never exceeds the track.
  const denom = budgetUsd > 0 ? Math.max(costUsd, budgetUsd) : costUsd;
  const visible = segments.filter((s) => s.costUsd > 0);

  return (
    <Box
      role="img"
      aria-label={`Cost ${formatUsd(costUsd)} of budget ${formatUsd(budgetUsd)} (${pct.toFixed(1)}%)`}
      data-testid="cost-breakdown-bar"
      sx={{
        position: 'relative',
        display: 'flex',
        gap: '2px',
        height: 10,
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: 'background.deeper',
        mb: 1,
      }}
    >
      {denom > 0 &&
        visible.map((s) => (
          <Tooltip key={s.key} title={`${s.label}: ${formatUsd(s.costUsd)}`}>
            <Box
              sx={{
                width: `${(s.costUsd / denom) * 100}%`,
                bgcolor: s.color,
                minWidth: s.costUsd > 0 ? 2 : 0,
              }}
            />
          </Tooltip>
        ))}
      {softPct > 0 && softPct < 100 && (
        <Box
          data-testid="soft-limit-tick"
          aria-hidden="true"
          sx={{
            position: 'absolute',
            left: `${softPct}%`,
            top: 0,
            bottom: 0,
            width: '2px',
            bgcolor: 'text.secondary',
            opacity: 0.9,
          }}
        />
      )}
    </Box>
  );
}

interface BreakdownRowProps {
  label: string;
  color: string;
  costUsd: number;
  totalUsd: number;
  mono?: boolean;
}

function BreakdownRow({ label, color, costUsd, totalUsd, mono = false }: BreakdownRowProps) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.5 }}>
      <Box
        sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: color, flexShrink: 0 }}
        aria-hidden="true"
      />
      <Typography
        variant="body2"
        sx={{ flexGrow: 1, ...(mono ? { fontFamily: MONO, fontSize: '0.75rem' } : {}) }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
        {formatUsd(costUsd)}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ fontFamily: 'monospace', width: 40, textAlign: 'right' }}
      >
        {pctOf(costUsd, totalUsd)}
      </Typography>
    </Stack>
  );
}

interface DimensionToggleProps {
  dimension: Dimension;
  onChange: (dimension: Dimension) => void;
}

function DimensionToggle({ dimension, onChange }: DimensionToggleProps) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '6px',
        overflow: 'hidden',
        bgcolor: 'background.deeper',
      }}
      role="group"
      aria-label="Cost breakdown dimension"
    >
      {(
        [
          ['feature', 'Feature'],
          ['model', 'Model'],
        ] as const
      ).map(([key, label]) => (
        <Box
          key={key}
          component="button"
          onClick={() => onChange(key)}
          aria-pressed={dimension === key}
          aria-label={`Break down by ${label.toLowerCase()}`}
          sx={{
            fontFamily: MONO,
            fontSize: '0.6875rem',
            fontWeight: 600,
            letterSpacing: '0.3px',
            textTransform: 'uppercase',
            px: 1.625,
            minHeight: 44,
            border: 'none',
            cursor: 'pointer',
            transition: 'all 140ms',
            ...(dimension === key
              ? {
                  bgcolor: 'rgba(217,119,87,0.10)',
                  color: '#D97757',
                }
              : {
                  background: 'transparent',
                  color: 'text.disabled',
                  '&:hover': { color: 'text.primary' },
                }),
          }}
        >
          {label}
        </Box>
      ))}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

export function CostBreakdownPanel({
  costUsd,
  budgetUsd,
  softLimitUsd,
  periodLabel,
  breakdown,
}: CostBreakdownPanelProps) {
  const [dimension, setDimension] = useState<Dimension>('feature');
  const [otherExpanded, setOtherExpanded] = useState(false);

  const pct = budgetUsd > 0 ? Math.min((costUsd / budgetUsd) * 100, 100) : 0;
  const softPct = budgetUsd > 0 ? Math.min((softLimitUsd / budgetUsd) * 100, 100) : 0;
  const tier = getColor(pct, softPct);

  const featureSegments: Segment[] = FEATURE_CONFIG.map(({ key, label, color }) => ({
    key,
    label,
    color,
    costUsd: breakdown[key],
  }));
  const modelSegments: Segment[] = breakdown.by_model.map((item: CostItem, index: number) => ({
    key: item.key,
    label: modelLabel(item.key),
    color: modelColor(item.key, index),
    costUsd: item.cost_usd,
  }));
  const segments = dimension === 'feature' ? featureSegments : modelSegments;

  return (
    <Box aria-live="polite" data-testid="cost-breakdown-panel" sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {periodLabel}
        </Typography>
        <Typography variant="body2" sx={{ color: `${tier}.main`, fontWeight: 600 }}>
          {pct.toFixed(1)}%
        </Typography>
      </Box>

      <SegmentedBudgetBar
        segments={segments}
        costUsd={costUsd}
        budgetUsd={budgetUsd}
        pct={pct}
        softPct={softPct}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {formatUsd(costUsd)}
          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
            / {formatUsd(budgetUsd)}
          </Typography>
        </Typography>
        <DimensionToggle dimension={dimension} onChange={setDimension} />
      </Box>

      {costUsd === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No LLM spend yet this month.
        </Typography>
      ) : dimension === 'feature' ? (
        <Stack spacing={0}>
          {featureSegments
            .filter((s) => s.key !== 'other')
            .map((s) => (
              <BreakdownRow
                key={s.key}
                label={s.label}
                color={s.color}
                costUsd={s.costUsd}
                totalUsd={costUsd}
              />
            ))}

          {/* Other — whole row is the expand/collapse control when there is
              a per-source split to reveal. */}
          {breakdown.other_sources.length > 0 ? (
            <>
              <Box
                component="button"
                onClick={() => setOtherExpanded((v) => !v)}
                aria-expanded={otherExpanded}
                aria-label="Toggle other cost sources"
                sx={{
                  all: 'unset',
                  cursor: 'pointer',
                  minHeight: 44,
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  transition: 'background-color 150ms',
                  '&:hover': { bgcolor: 'action.hover' },
                  '&:focus-visible': {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: 2,
                  },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: 0.5,
                      bgcolor: NEUTRAL_GRAY,
                      flexShrink: 0,
                    }}
                    aria-hidden="true"
                  />
                  <Typography variant="body2" sx={{ flexGrow: 1, textAlign: 'left' }}>
                    Other
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: 0.75 }}
                    >
                      {otherExpanded ? '▾' : '▸'}
                    </Typography>
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {formatUsd(breakdown.other)}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontFamily: 'monospace', width: 40, textAlign: 'right' }}
                  >
                    {pctOf(breakdown.other, costUsd)}
                  </Typography>
                </Stack>
              </Box>
              <Collapse in={otherExpanded} timeout={200}>
                <Stack spacing={0} sx={{ pl: 2.5 }}>
                  {breakdown.other_sources.map((item: CostItem) => (
                    <BreakdownRow
                      key={item.key}
                      label={item.key}
                      color="transparent"
                      costUsd={item.cost_usd}
                      totalUsd={breakdown.other}
                      mono
                    />
                  ))}
                </Stack>
              </Collapse>
            </>
          ) : (
            <BreakdownRow
              label="Other"
              color={NEUTRAL_GRAY}
              costUsd={breakdown.other}
              totalUsd={costUsd}
            />
          )}
        </Stack>
      ) : (
        <Stack spacing={0}>
          {modelSegments.map((s) => (
            <BreakdownRow
              key={s.key}
              label={s.label}
              color={s.color}
              costUsd={s.costUsd}
              totalUsd={costUsd}
              mono
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}
